/**
 * perfusionCT.js — перфузия по динамической КТ (инсультный протокол):
 * CBF, CBV, MTT, Tmax. Чистая математика, ни одного импорта; проверяется в
 * node на синтетике с известным ответом (tests/perfusion_test.mjs).
 *
 * ФИЗИКА, коротко и без украшений. Динамическая КТ снимает один уровень много
 * раз, пока идёт болюс контраста. Прирост плотности вокселя над фоном ~
 * концентрации контраста в ткани Ct(t); прирост в артерии — артериальная
 * входная функция Ca(t) (АИФ). Модель разведения:
 *
 *     Ct(t) = CBF · (Ca ⊛ R)(t),   R(0)=1, R не возрастает
 *
 * где R(t) — доля контраста, ещё не покинувшей воксель к моменту t. Отсюда:
 *   CBF — из максимума восстановленной k(t) = CBF·R(t);
 *   CBV = κ/ρ · ∫Ct / ∫Ca — объём крови (центральная теорема объёма);
 *   MTT = ∫k / max(k) — среднее время прохождения;
 *   Tmax = argmax k(t) — задержка прихода болюса (главная карта пенумбры).
 *
 * ДЕКОНВОЛЮЦИЯ — блочно-циркулянтная (Wu 2003, oSVD-семейство), нечувствительная
 * к задержке болюса: ряды дополняются нулями до L ≥ 2N, свёртка становится
 * циркулянтной, а у циркулянтной матрицы сингулярные числа — это в точности
 * |FFT(Ca)|. Поэтому «SVD с усечением» здесь считается БЕЗ построения матриц:
 *
 *     k = IFFT( FFT(Ct) / FFT(Ca) ), частоты с |FFT(Ca)| < Psvd·max — в ноль.
 *
 * Это математически ТА ЖЕ операция, что cSVD с порогом Psvd, только за
 * O(L·logL) на воксель вместо O(L³). Порог по умолчанию 0.15 — обычное
 * значение для инсультного протокола; без усечения деконволюция взрывается
 * на шуме (деление на почти-нулевые частоты).
 *
 * ЕДИНИЦЫ. κ = (1−Hct_кр)/(1−Hct_кап) = (1−0.45)/(1−0.25) ≈ 0.733 — поправка
 * на разный гематокрит в крупных и мелких сосудах; ρ = 1.04 г/мл — плотность
 * ткани мозга. CBF выдаётся в мл/100 г/мин, CBV — в мл/100 г, MTT и Tmax — в с.
 *
 * ЧЕСТНОСТЬ ОЦЕНОК — числа с фантома (tests/perfusion_test.mjs), а не «примерно»:
 *   · ∫k и CBV точны: на фантоме расходятся с истиной меньше чем на 3%
 *     (усечение частот сохраняет площадь, оно режет только форму пика);
 *   · CBF = max k ЗАНИЖЕН: усечение сглаживает скачок R(0)=1, и пик k падает.
 *     На фантоме (MTT 4 с, шаг 1 с) восстанавливается 60–70% истинного CBF;
 *     при длинном MTT смещение мягче (MTT 10 с → ~90%). Это свойство ВСЕГО
 *     семейства SVD-деконволюций, описанное в литературе; клинические пороги
 *     (rCBF < 30% от здоровой стороны) построены на ОТНОШЕНИЯХ, где общий
 *     множитель сокращается. Передискретизация и сглаживание АИФ проверены
 *     экспериментом — не лечат;
 *   · MTT = CBV/CBF наследует обратное смещение (завышен там, где CBF занижен);
 *   · Tmax несёт метод-зависимый пьедестал ~MTT/2 (сглаженный пик съезжает
 *     вправо), но РАЗНОСТИ задержек передаются точно: на фантоме задержка 3 с
 *     даёт сдвиг Tmax ровно на 3 с. Порог пенумбры «Tmax > 6 с» в литературе
 *     откалиброван на таких же SVD-оценках.
 */

/** Ближайшая сверху степень двойки. */
export function следующаяСтепеньДвойки(n) {
  let p = 1;
  while (p < n) p <<= 1;
  return p;
}

/**
 * БПФ по основанию 2, на месте. Длина — степень двойки, иначе отказ.
 * Классика Кули—Тьюки: перестановка бит-реверсом, затем «бабочки».
 */
export function бпф(re, im, обратное = false) {
  const n = re.length;
  if (n !== im.length || (n & (n - 1)) !== 0) throw new Error('бпф: длина не степень двойки');
  // Перестановка бит-реверсом.
  for (let i = 1, j = 0; i < n; i++) {
    let бит = n >> 1;
    for (; j & бит; бит >>= 1) j ^= бит;
    j ^= бит;
    if (i < j) {
      let t = re[i]; re[i] = re[j]; re[j] = t;
      t = im[i]; im[i] = im[j]; im[j] = t;
    }
  }
  for (let длина = 2; длина <= n; длина <<= 1) {
    const угол = (обратное ? 1 : -1) * 2 * Math.PI / длина;
    const wRe = Math.cos(угол), wIm = Math.sin(угол);
    for (let i = 0; i < n; i += длина) {
      let кRe = 1, кIm = 0;
      for (let j = 0; j < длина / 2; j++) {
        const aRe = re[i + j], aIm = im[i + j];
        const bRe = re[i + j + длина / 2] * кRe - im[i + j + длина / 2] * кIm;
        const bIm = re[i + j + длина / 2] * кIm + im[i + j + длина / 2] * кRe;
        re[i + j] = aRe + bRe; im[i + j] = aIm + bIm;
        re[i + j + длина / 2] = aRe - bRe; im[i + j + длина / 2] = aIm - bIm;
        const тRe = кRe * wRe - кIm * wIm;
        кIm = кRe * wIm + кIm * wRe; кRe = тRe;
      }
    }
  }
  if (обратное) for (let i = 0; i < n; i++) { re[i] /= n; im[i] /= n; }
  return { re, im };
}

/**
 * Деконволюция одного вокселя циркулянтом с усечением частот.
 *
 * @param {ArrayLike<number>} ct — кривая ткани (за вычетом фона), длина N
 * @param {object} аифПодготовленная — из подготовитьАИФ(): {спектрRe, спектрIm,
 *        модуль, порогМодуля, L, dt}
 * @returns {{k: Float32Array, максК: number, iМакс: number}}
 */
export function деконволюцияВокселя(ct, аиф) {
  const { спектрRe, спектрIm, модуль, порогМодуля, L } = аиф;
  const re = new Float64Array(L), im = new Float64Array(L);
  for (let i = 0; i < ct.length; i++) re[i] = ct[i];
  бпф(re, im, false);
  for (let i = 0; i < L; i++) {
    if (модуль[i] < порогМодуля) { re[i] = 0; im[i] = 0; continue; }
    // Деление комплексных: Ct(ω) / Ca(ω)
    const d = модуль[i] * модуль[i];
    const aRe = re[i], aIm = im[i];
    re[i] = (aRe * спектрRe[i] + aIm * спектрIm[i]) / d;
    im[i] = (aIm * спектрRe[i] - aRe * спектрIm[i]) / d;
  }
  бпф(re, im, true);
  const k = new Float32Array(L);
  let максК = -Infinity, iМакс = 0;
  for (let i = 0; i < L; i++) {
    k[i] = re[i];
    if (re[i] > максК) { максК = re[i]; iМакс = i; }
  }
  return { k, максК, iМакс };
}

/**
 * Подготовка АИФ: спектр, модуль, порог усечения. Считается один раз на все
 * воксели — в этом вся скорость метода.
 * @param {ArrayLike<number>} ca — артериальная кривая (за вычетом фона), длина N
 * @param {number} dt — шаг по времени, с
 * @param {number} [psvd=0.15] — порог усечения, доля от max|FFT(Ca)|
 */
export function подготовитьАИФ(ca, dt, psvd = 0.15) {
  const N = ca.length;
  if (N < 8) return { ok: false, причина: `кадров по времени всего ${N} — для деконволюции мало (нужно ≥ 8)` };
  let максCa = -Infinity;
  for (let i = 0; i < N; i++) if (ca[i] > максCa) максCa = ca[i];
  if (!(максCa > 0)) return { ok: false, причина: 'артериальная кривая без подъёма — это не АИФ' };
  const L = следующаяСтепеньДвойки(2 * N);
  const re = new Float64Array(L), im = new Float64Array(L);
  // Свёртка дискретизуется как Ct = dt · (Ca ⊛ R): dt входит в спектр АИФ,
  // тогда k выходит сразу в 1/с.
  for (let i = 0; i < N; i++) re[i] = ca[i] * dt;
  бпф(re, im, false);
  const модуль = new Float64Array(L);
  let максМод = 0;
  for (let i = 0; i < L; i++) {
    модуль[i] = Math.hypot(re[i], im[i]);
    if (модуль[i] > максМод) максМод = модуль[i];
  }
  return { ok: true, спектрRe: re, спектрIm: im, модуль, порогМодуля: psvd * максМод, L, dt, N };
}

/** Показатели одного вокселя из восстановленной k(t). κ/ρ по умолчанию — мозг.
 *  MTT — по центральной теореме объёма (CBV/CBF), т.е. (∫Ct/∫Ca)/max k, когда
 *  отношение интегралов дано, иначе ∫k/max k — та же теорема через k. */
export function показателиИзК(k, максК, iМакс, dt, { каппа = 0.733, плотность = 1.04, интегралОтношение = null } = {}) {
  if (!(максК > 0)) return { cbf: 0, cbv: 0, mtt: 0, tmax: 0 };
  let интегралК = 0;
  // Интегрируем только «содержательную» половину (вторая — хвост циркулянта).
  const половина = k.length >> 1;
  for (let i = 0; i < половина; i++) интегралК += Math.max(0, k[i]);
  интегралК *= dt;
  const множитель = (каппа / плотность) * 100;         // → на 100 г ткани
  const объём = интегралОтношение !== null ? интегралОтношение : интегралК;   // = CBF·MTT, с
  const cbf = максК * множитель * 60;                   // 1/с → мл/100г/мин
  const cbv = объём * множитель;                        // мл/100г
  const mtt = объём / максК;                            // с — центральная теорема объёма
  const tmax = iМакс * dt;                              // с
  return { cbf, cbv, mtt, tmax };
}

/**
 * Автопоиск кандидата в АИФ по кадрам одного среза.
 *
 * Артерия на перфузионной КТ — это воксель, где подъём РАННИЙ, ВЫСОКИЙ и
 * УЗКИЙ. Оценка = амплитуда / (время до пика + полуширина): вены дают высокий,
 * но ПОЗДНИЙ и широкий пик — знаменатель их топит. Возвращаются лучшие
 * кандидаты, врач волен указать артерию сам щелчком.
 *
 * @param {Array<Float32Array>} кадры — по времени, каждый длиной ширина×высота
 * @param {number} ширина
 * @param {number} базлайнКадров — сколько первых кадров считать фоном
 * @param {number} [сколько=5]
 */
export function кандидатыАИФ(кадры, ширина, базлайнКадров, сколько = 5) {
  const T = кадры.length, всего = кадры[0].length;
  const лучшие = [];   // {оценка, i, амплитуда, доПикаКадров, полуширинаКадров}
  for (let i = 0; i < всего; i++) {
    let фон = 0;
    for (let t = 0; t < базлайнКадров; t++) фон += кадры[t][i];
    фон /= базлайнКадров;
    let макс = -Infinity, tМакс = 0;
    for (let t = 0; t < T; t++) {
      const v = кадры[t][i] - фон;
      if (v > макс) { макс = v; tМакс = t; }
    }
    if (!(макс > 50)) continue;   // меньше 50 HU подъёма — не артерия
    // Полуширина: сколько кадров кривая держится выше половины пика.
    let полуширина = 0;
    for (let t = 0; t < T; t++) if (кадры[t][i] - фон >= макс / 2) полуширина++;
    const оценка = макс / (tМакс + полуширина + 1);
    if (лучшие.length < сколько || оценка > лучшие[лучшие.length - 1].оценка) {
      лучшие.push({ оценка, i, амплитуда: макс, доПикаКадров: tМакс, полуширинаКадров: полуширина });
      лучшие.sort((а, б) => б.оценка - а.оценка);
      if (лучшие.length > сколько) лучшие.pop();
    }
  }
  return лучшие.map((к) => ({ ...к, x: к.i % ширина, y: Math.floor(к.i / ширина) }));
}

/** Сколько первых кадров — фон: до начала подъёма АИФ (порог 10% её пика). */
export function оценитьБазлайн(аифСырая) {
  let макс = -Infinity, мин = Infinity;
  for (const v of аифСырая) { if (v > макс) макс = v; if (v < мин) мин = v; }
  const порог = мин + (макс - мин) * 0.1;
  let n = 0;
  while (n < аифСырая.length && аифСырая[n] <= порог) n++;
  return Math.max(1, Math.min(n, аифСырая.length - 2));
}

/**
 * Карты перфузии одного среза.
 *
 * @param {Array<Float32Array>} кадры — плотности по времени (HU), отсортированы
 * @param {ArrayLike<number>} времена — секунды кадров (той же длины)
 * @param {{x:number,y:number}|Float32Array} аифГде — точка артерии или готовая кривая
 * @param {object} [п] — { ширина, psvd, каппа, плотность, минПодъёмHU=5 }
 * @returns {{ok, cbf, cbv, mtt, tmax: Float32Array, всего, посчитано, аиф}}
 */
export function картыПерфузии(кадры, времена, аифГде, п = {}) {
  const T = кадры?.length || 0;
  if (T < 8) return { ok: false, причина: `кадров по времени ${T} — перфузия считается от 8` };
  const всего = кадры[0].length;
  const ширина = п.ширина || Math.round(Math.sqrt(всего));
  // Шаг по времени: медиана разностей. Неровный шаг (пауза сканера) не валит
  // расчёт, но честно отмечается.
  const шаги = [];
  for (let t = 1; t < T; t++) шаги.push(времена[t] - времена[t - 1]);
  шаги.sort((а, б) => а - б);
  const dt = шаги[шаги.length >> 1];
  if (!(dt > 0)) return { ok: false, причина: 'времена кадров не возрастают' };
  const неровно = шаги[шаги.length - 1] > dt * 1.5;

  // АИФ: точка или готовая кривая.
  let аифСырая;
  if (аифГде && Number.isFinite(аифГде.x) && Number.isFinite(аифГде.y)) {
    const i = аифГде.x + аифГде.y * ширина;
    аифСырая = new Float64Array(T);
    for (let t = 0; t < T; t++) аифСырая[t] = кадры[t][i];
  } else if (аифГде?.length === T) {
    аифСырая = аифГде;
  } else {
    return { ok: false, причина: 'не указана артерия (точкой или кривой)' };
  }
  const базлайнК = п.базлайнКадров || оценитьБазлайн(аифСырая);
  let фонАиф = 0;
  for (let t = 0; t < базлайнК; t++) фонАиф += аифСырая[t];
  фонАиф /= базлайнК;
  const ca = new Float64Array(T);
  for (let t = 0; t < T; t++) ca[t] = Math.max(0, аифСырая[t] - фонАиф);

  const аиф = подготовитьАИФ(ca, dt, п.psvd ?? 0.15);
  if (!аиф.ok) return аиф;
  let интегралCa = 0;
  for (let t = 0; t < T; t++) интегралCa += ca[t];
  интегралCa *= dt;
  if (!(интегралCa > 0)) return { ok: false, причина: 'площадь под АИФ нулевая' };

  const cbf = new Float32Array(всего), cbv = new Float32Array(всего);
  const mtt = new Float32Array(всего), tmax = new Float32Array(всего);
  const ct = new Float64Array(T);
  const минПодъём = п.минПодъёмHU ?? 5;
  let посчитано = 0;
  for (let i = 0; i < всего; i++) {
    let фон = 0;
    for (let t = 0; t < базлайнК; t++) фон += кадры[t][i];
    фон /= базлайнК;
    let макс = -Infinity, интегралCt = 0;
    for (let t = 0; t < T; t++) {
      const v = кадры[t][i] - фон;
      ct[t] = v;
      if (v > макс) макс = v;
      интегралCt += Math.max(0, v);
    }
    if (макс < минПодъём) continue;   // воздух, кость, воксель без контраста
    интегралCt *= dt;
    const { k, максК, iМакс } = деконволюцияВокселя(ct, аиф);
    const пок = показателиИзК(k, максК, iМакс, dt, {
      каппа: п.каппа, плотность: п.плотность,
      интегралОтношение: интегралCt / интегралCa,
    });
    cbf[i] = пок.cbf; cbv[i] = пок.cbv; mtt[i] = пок.mtt; tmax[i] = пок.tmax;
    посчитано++;
  }
  return { ok: true, cbf, cbv, mtt, tmax, всего, посчитано, dt, базлайнКадров: базлайнК, неровныйШаг: неровно, аифКривая: ca };
}
